import { BuyerProject } from "@/types/buyerProject";
import { ProcessDetails } from "@/types/processDetails";
import { PropertyType } from "@/types/property";
import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";

// Helper function to build ID-to-boolean maps
const buildIdMap = <T>(items: T[], idSelector: (item: T) => string | undefined): Record<string, boolean> => {
	return items.reduce(
		(acc, item) => {
			const id = idSelector(item);
			if (id) acc[id] = true;
			return acc;
		},
		{} as Record<string, boolean>,
	);
};

// Basic types - keeping it simple for now
export interface BuyerUnit {
	id: string;
	unit?: {
		id: string;
	};
	processDetails?: ProcessDetails[];
	cancellation?: {
		flag: boolean;
		reason?: string;
	};
}

export interface BuyerPropertiesState {
	buyerUnits: BuyerUnit[];
	buyerProjects?: BuyerProject[];

	// Pre-computed shortlisted units for faster access
	shortlistedUnits: BuyerUnit[];
	shortlistedProjects?: BuyerProject[];

	// Pre-computed shortlisted IDs for O(1) access
	shortlistedUnitIds: Record<string, boolean>;
	shortlistedProjectIds: Record<string, boolean>;

	// Metadata
	lastFetchedAt: string | null;
	buyerGroupId: string | null;

	// Basic actions
	setBuyerUnits: (units: BuyerUnit[], buyerGroupId: string) => void;
	setBuyerProjects: (projects: BuyerProject[], buyerGroupId: string) => void;
	setShortlistedProjects: (projects: BuyerProject[]) => void;
	// Simple utility getters
	isPropertyShortlisted: (propertyId: string) => boolean;
	isProjectShortlisted: (projectId: string) => boolean;

	// Process stage checkers
	isPropertyInDecisionStage: (propertyId: string) => boolean;

	// Optimistic updates for immediate UI response (suggestion cards)
	optimisticAddToShortlist: (propertyId: string, type: PropertyType) => void;
	optimisticRemoveFromShortlist: (propertyId: string, type: PropertyType) => void;

	// Manual optimistic updates for different mutation contexts (property overlay, etc.)
	optimisticAddToShortlistManual: (propertyId: string, type: PropertyType) => void;
}

export const useBuyerPropertiesStore = create<BuyerPropertiesState>()(
	devtools(
		immer((set, get) => ({
			// Initial state
			buyerUnits: [],
			buyerProjects: [],
			shortlistedUnits: [],
			shortlistedProjects: [],
			shortlistedUnitIds: {},
			shortlistedProjectIds: {},
			lastFetchedAt: null,
			buyerGroupId: null,

			// Actions
			setBuyerUnits: (units: BuyerUnit[], buyerGroupId: string) => {
				set(state => {
					state.buyerUnits = units;
					state.buyerGroupId = buyerGroupId;
					state.lastFetchedAt = new Date().toISOString();

					// Pre-compute shortlisted units for faster access
					state.shortlistedUnits = units.filter(unit =>
						unit.processDetails?.some(pd => pd.process === "shortlisting" && pd.stage === "done"),
					);

					state.shortlistedUnitIds = buildIdMap(state.shortlistedUnits, (unit: BuyerUnit) => unit.unit?.id);
				});
			},

			setBuyerProjects: (projects: BuyerProject[], buyerGroupId: string) => {
				set(state => {
					state.buyerProjects = projects;
					state.buyerGroupId = buyerGroupId;
					state.lastFetchedAt = new Date().toISOString();

					// NOTE: We no longer compute shortlistedProjects here because we have a dedicated
					// getShortlistedProjects query that provides accurate data from the server.
					// The shortlistedProjects state is now managed by setShortlistedProjects() only.
				});
			},

			setShortlistedProjects: (projects: BuyerProject[]) => {
				set(state => {
					// Merge with existing optimistic updates instead of replacing
					// Keep temporary projects that were optimistically added
					const existingOptimisticProjects = (state.shortlistedProjects || []).filter(p =>
						p.id.startsWith("temp-project-"),
					);

					// Only keep optimistic projects that aren't already in the server data
					const serverProjectIds = new Set(projects.map(p => p.project?.id));
					const validOptimisticProjects = existingOptimisticProjects.filter(p => !serverProjectIds.has(p.project?.id));

					// Merge server data with valid optimistic updates
					state.shortlistedProjects = [...projects, ...validOptimisticProjects];

					state.shortlistedProjectIds = buildIdMap(
						state.shortlistedProjects ?? [],
						(project: BuyerProject) => project.project?.id,
					);
				});
			},

			// Simple utility getters
			isPropertyShortlisted: (propertyId: string) => {
				const state = get();

				// Check optimized maps first
				if (state.shortlistedUnitIds[propertyId]) return true;
				if (state.shortlistedProjectIds && state.shortlistedProjectIds[propertyId]) return true;

				return false;
			},

			isProjectShortlisted: (projectId: string) => {
				const state = get();
				return !!state.shortlistedProjectIds[projectId];
			},

			// Process stage checkers
			isPropertyInDecisionStage: (propertyId: string) => {
				const state = get();

				// Check in units
				const unitInDecision = state.buyerUnits.some(unit => {
					const isMatchingUnit = unit.unit?.id === propertyId;
					const hasDecisionStage = unit.processDetails?.some(pd => pd.process === "decision" && pd.stage === "done");
					return isMatchingUnit && hasDecisionStage;
				});

				if (unitInDecision) {
					return true;
				}

				// Check in projects
				if (state.buyerProjects) {
					const projectInDecision = state.buyerProjects.some(project => {
						const isMatchingProject = project.project?.id === propertyId;
						const hasDecisionStage = project.processDetails?.some(
							pd => pd.process === "decision" && pd.stage === "done",
						);
						return isMatchingProject && hasDecisionStage;
					});
					return projectInDecision;
				}
				return false;
			},

			// Optimistic updates for immediate UI response
			optimisticAddToShortlist: (propertyId: string, type: PropertyType) => {
				set(state => {
					if (type === PropertyType.UNIT) {
						// Handle unit shortlisting
						let existingUnitIndex = state.buyerUnits.findIndex(unit => unit.unit?.id === propertyId);

						if (existingUnitIndex !== -1) {
							// Update existing unit
							const unit = state.buyerUnits[existingUnitIndex];
							if (!unit.processDetails) {
								unit.processDetails = [];
							}

							// Remove any existing shortlisting process
							unit.processDetails = unit.processDetails.filter(pd => pd.process !== "shortlisting");

							// Add new shortlisting process
							unit.processDetails.push({
								process: "shortlisting",
								status: "active",
								stage: "done",
								incomplete: false,
								appointmentDate: new Date(),
								visitExec: "",
								updatedAt: new Date(),
								updatedBy: "",
							});
						} else {
							// Create new unit entry
							const newUnit = {
								id: `temp-unit-${propertyId}-${Date.now()}`,
								unit: { id: propertyId },
								processDetails: [
									{
										process: "shortlisting",
										status: "active",
										stage: "done",
										incomplete: false,
										appointmentDate: new Date(),
										visitExec: "",
										updatedAt: new Date(),
										updatedBy: "",
									},
								],
							};
							state.buyerUnits.push(newUnit);
						}

						// Recompute shortlisted units
						state.shortlistedUnits = state.buyerUnits.filter(unit =>
							unit.processDetails?.some(pd => pd.process === "shortlisting" && pd.stage === "done"),
						);
						state.shortlistedUnitIds = buildIdMap(state.shortlistedUnits, (unit: BuyerUnit) => unit.unit?.id);
					} else if (type === PropertyType.PROJECT) {
						// Handle project shortlisting
						if (!state.buyerProjects) {
							state.buyerProjects = [];
						}

						let existingProjectIndex = state.buyerProjects.findIndex(project => project.project?.id === propertyId);

						if (existingProjectIndex !== -1) {
							// Update existing project
							const project = state.buyerProjects[existingProjectIndex];
							if (!project.processDetails) {
								project.processDetails = [];
							}

							// Remove any existing shortlisting process
							project.processDetails = project.processDetails.filter(pd => pd.process !== "shortlisting");

							// Add new shortlisting process
							project.processDetails.push({
								process: "shortlisting",
								status: "active",
								stage: "done",
								incomplete: false,
								appointmentDate: new Date(),
								visitExec: "",
								updatedAt: new Date(),
								updatedBy: "",
							});
						} else {
							// Create new project entry
							const processDetail = {
								process: "shortlisting",
								status: "active",
								stage: "done",
								incomplete: false,
								appointmentDate: new Date(),
								visitExec: "",
								updatedAt: new Date(),
								updatedBy: "",
							};
							const newProject = {
								id: `temp-project-${propertyId}-${Date.now()}`,
								project: { id: propertyId, name: "" },
								sellerProject: { id: propertyId },
								group: { id: state.buyerGroupId || "" },
								processDetails: [processDetail],
								currentProcessDetails: processDetail,
								cancellation: { flag: false },
							};
							state.buyerProjects.push(newProject);
						}

						// Recompute shortlisted projects
						state.shortlistedProjects = state.buyerProjects.filter(project =>
							project.processDetails?.some(pd => pd.process === "shortlisting" && pd.stage === "done"),
						);
						state.shortlistedProjectIds = buildIdMap(
							state.shortlistedProjects,
							(project: BuyerProject) => project.project?.id,
						);
					}
				});
			},

			optimisticRemoveFromShortlist: (propertyId: string, type: PropertyType) => {
				set(state => {
					if (type === PropertyType.UNIT) {
						// Handle unit shortlist removal
						const unitIndex = state.buyerUnits.findIndex(unit => unit.unit?.id === propertyId);

						if (unitIndex !== -1) {
							// Remove shortlisting from unit
							const unit = state.buyerUnits[unitIndex];
							if (unit.processDetails) {
								unit.processDetails = unit.processDetails.filter(pd => pd.process !== "shortlisting");
							}
						}

						// Recompute shortlisted units
						state.shortlistedUnits = state.buyerUnits.filter(unit =>
							unit.processDetails?.some(pd => pd.process === "shortlisting" && pd.stage === "done"),
						);
						state.shortlistedUnitIds = buildIdMap(state.shortlistedUnits, (unit: BuyerUnit) => unit.unit?.id);
					} else if (type === PropertyType.PROJECT) {
						// Handle project shortlist removal
						if (state.buyerProjects) {
							const projectIndex = state.buyerProjects.findIndex(project => project.project?.id === propertyId);

							if (projectIndex !== -1) {
								// Remove shortlisting from project
								const project = state.buyerProjects[projectIndex];
								if (project.processDetails) {
									project.processDetails = project.processDetails.filter(pd => pd.process !== "shortlisting");
								}
							}

							// Recompute shortlisted projects
							state.shortlistedProjects = state.buyerProjects.filter(project =>
								project.processDetails?.some(pd => pd.process === "shortlisting" && pd.stage === "done"),
							);
							state.shortlistedProjectIds = buildIdMap(
								state.shortlistedProjects,
								(project: BuyerProject) => project.project?.id,
							);
						}
					}
				});
			},

			// Manual optimistic updates - same logic but separate methods for different contexts
			optimisticAddToShortlistManual: (propertyId: string, type: PropertyType) => {
				set(state => {
					if (type === PropertyType.UNIT) {
						// Handle unit shortlisting
						let existingUnitIndex = state.buyerUnits.findIndex(unit => unit.unit?.id === propertyId);

						if (existingUnitIndex !== -1) {
							// Update existing unit
							const unit = state.buyerUnits[existingUnitIndex];
							if (!unit.processDetails) {
								unit.processDetails = [];
							}

							// Remove any existing shortlisting process
							unit.processDetails = unit.processDetails.filter(pd => pd.process !== "shortlisting");

							// Add new shortlisting process
							unit.processDetails.push({
								process: "shortlisting",
								status: "active",
								stage: "done",
								incomplete: false,
								appointmentDate: new Date(),
								visitExec: "",
								updatedAt: new Date(),
								updatedBy: "",
							});
						} else {
							// Create new unit entry
							const newUnit = {
								id: `temp-unit-manual-${propertyId}-${Date.now()}`,
								unit: { id: propertyId },
								processDetails: [
									{
										process: "shortlisting",
										status: "active",
										stage: "done",
										incomplete: false,
										appointmentDate: new Date(),
										visitExec: "",
										updatedAt: new Date(),
										updatedBy: "",
									},
								],
							};
							state.buyerUnits.push(newUnit);
						}

						// Recompute shortlisted units
						state.shortlistedUnits = state.buyerUnits.filter(unit =>
							unit.processDetails?.some(pd => pd.process === "shortlisting" && pd.stage === "done"),
						);
						state.shortlistedUnitIds = buildIdMap(state.shortlistedUnits, (unit: BuyerUnit) => unit.unit?.id);
					} else if (type === PropertyType.PROJECT) {
						// Handle project shortlisting
						if (!state.buyerProjects) {
							state.buyerProjects = [];
						}

						let existingProjectIndex = state.buyerProjects.findIndex(project => project.project?.id === propertyId);

						if (existingProjectIndex !== -1) {
							// Update existing project
							const project = state.buyerProjects[existingProjectIndex];
							if (!project.processDetails) {
								project.processDetails = [];
							}

							// Remove any existing shortlisting process
							project.processDetails = project.processDetails.filter(pd => pd.process !== "shortlisting");

							// Add new shortlisting process
							project.processDetails.push({
								process: "shortlisting",
								status: "active",
								stage: "done",
								incomplete: false,
								appointmentDate: new Date(),
								visitExec: "",
								updatedAt: new Date(),
								updatedBy: "",
							});
						} else {
							// Create new project entry
							const processDetail = {
								process: "shortlisting",
								status: "active",
								stage: "done",
								incomplete: false,
								appointmentDate: new Date(),
								visitExec: "",
								updatedAt: new Date(),
								updatedBy: "",
							};

							const newProject = {
								id: `temp-project-manual-${propertyId}-${Date.now()}`,
								project: { id: propertyId, name: "" },
								sellerProject: { id: propertyId },
								group: { id: state.buyerGroupId || "" },
								processDetails: [processDetail],
								currentProcessDetails: processDetail,
								cancellation: { flag: false },
							};
							state.buyerProjects.push(newProject);
						}

						// Recompute shortlisted projects
						state.shortlistedProjects = state.buyerProjects.filter(project =>
							project.processDetails?.some(pd => pd.process === "shortlisting" && pd.stage === "done"),
						);
						state.shortlistedProjectIds = buildIdMap(
							state.shortlistedProjects,
							(project: BuyerProject) => project.project?.id,
						);
					}
				});
			},
		})),
		{ name: "buyer-properties-store" },
	),
);
